// Expenses tracker.
// Mirrors IncentivesPanel except:
//   * TEAMS = Team 1, Team 2, HQ  (no Adherence branch)
//   * "Description" (dropdown, editable) replaces first/last name
//   * Each record supports a file upload (PDF / image) with an in-panel preview
//     — mimics the /pharmacy/partners invoice upload+preview UX.
// Backed by /api/consultations/expenses/*.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Pencil, Trash2, Check, Upload, FileText, Eye, X as XIcon, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { api } from "@/lib/consult/api";
import TableColumnHeader from "./TableColumnHeader";
import { useTableView, applyTableView, uniqueValues, uniqueValuePairs, hasActiveFilters } from "./tableView";

const DARK_GREEN = "#0F3B2E";
const LABEL_GREEN = "#2D5A4C";
const TEXT = "#1C2B26";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const NAVY = "#0F3B2E";
const TAG_CASH = "#2E7D5B";
const TAG_BANK = "#4A6FB0";
const TAG_CC = "#7C4A9F";
const TAG_COMPANY = "#A66500";

// Fallback until the shared staff/team registry loads — new staff added in
// Users admin appear as their own Team N (XX) buttons automatically.
const DEFAULT_TEAMS = [
  { key: "team_1", label: "Team 1 (G)" },
  { key: "team_2", label: "Team 2 (R)" },
  { key: "team_3", label: "Team 3 (M)" },
  { key: "hq", label: "HQ" },
];

// Module-level caches — this panel unmounts whenever its tab is hidden, and
// without them every flip back flashes "Loading…" and pops the locked team
// segment into the strip a beat late.
const TEAMS_CACHE = { teams: null };
const DATA_CACHE = new Map(); // `${team}|${month}|${day}` -> { records, summary, descriptions, topups, methods }

const labelStyle = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.16em",
  textTransform: "uppercase", color: LABEL_GREEN, margin: 0, marginBottom: 4,
};

// Labels above a boxed field: pad so the label text starts exactly where the
// field's text starts (1px border + 12px horizontal padding of inputStyle).
const fieldLabelStyle = { ...labelStyle, paddingLeft: 13 };

const inputStyle = {
  width: "100%", maxWidth: "100%", minWidth: 0, height: 38,
  padding: "0 12px", fontSize: 14, fontFamily: "inherit",
  borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff",
  color: TEXT, boxSizing: "border-box",
};

const buildMonths = () => {
  const list = [];
  const now = new Date();
  for (let i = -6; i <= 3; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString(undefined, { month: "short", year: "numeric" });
    list.push({ key, label });
  }
  return list;
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fromYMD = (ymd) => {
  const [y, m, d] = (ymd || "").split("-").map(Number);
  if (!y) return null;
  return new Date(y, m - 1, d);
};

const toYMD = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const money = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
};

function DatePickerButton({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? fromYMD(value) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          style={{
            ...inputStyle,
            textAlign: "left",
            display: "flex", alignItems: "center",
            cursor: "pointer",
          }}
        >
          {selected ? format(selected, "MMM d, yyyy") : "Pick a date"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2" data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          selected={selected || undefined}
          onSelect={(d) => {
            if (d) {
              onChange(toYMD(d));
              setOpen(false);
            }
          }}
          className="p-0"
        />
      </PopoverContent>
    </Popover>
  );
}

// Push a history marker while `open` so the phone's Back button (or
// Safari swipe-back) closes the dialog instead of leaving the page.
// Mirrors the same pattern used in SchedulePreviewDialog.
function useBackClosesDialog(open, onClose, key) {
  // Keep a ref to the latest onClose so re-renders that change the callback
  // identity don't re-run the effect (and don't accidentally call
  // history.back() on every field change, which closes the dialog).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const marker = { [`__hepcureExpenses_${key}`]: true };
    try { window.history.pushState(marker, ""); } catch { /* ignore */ }
    const onPop = () => { if (closeRef.current) closeRef.current(); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      try {
        const s = window.history.state;
        if (s && s[`__hepcureExpenses_${key}`]) window.history.back();
      } catch { /* ignore */ }
    };
    // Only re-attach when the dialog actually opens/closes.
  }, [open, key]);
}

// ============================================================================
export default function ExpensesPanel({ refreshKey = 0, lockTeam = null, lockTeamLabel = null }) {
  const months = useMemo(() => buildMonths(), []);
  const [team, setTeam] = useState(lockTeam || DEFAULT_TEAMS[0].key);
  // Live team list from the shared registry (Team 4 (XX), Team 5 (XX), …
  // appear here as soon as new staff are added in Users admin).
  const [teams, setTeams] = useState(TEAMS_CACHE.teams || DEFAULT_TEAMS);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff-teams");
        if (Array.isArray(data?.teams) && data.teams.length) {
          const merged = [...data.teams.map((t) => ({ key: t.key, label: t.label })), { key: "hq", label: "HQ" }];
          TEAMS_CACHE.teams = merged;
          setTeams(merged);
        }
      } catch { /* keep defaults */ }
    })();
  }, []);
  // staff preview: lock the strip to the staff member's own team
  useEffect(() => { if (lockTeam && team !== lockTeam) setTeam(lockTeam); }, [lockTeam, team]);
  const [month, setMonth] = useState(currentMonthKey());
  const [day, setDay] = useState("all");

  // Hydrate from the module cache on remount so the panel renders instantly
  // (no "Loading…" flash) while the fresh fetch runs in the background.
  const cached0 = DATA_CACHE.get(`${lockTeam || DEFAULT_TEAMS[0].key}|${currentMonthKey()}|all`) || null;
  const [records, setRecords] = useState(cached0?.records || []);
  const [summary, setSummary] = useState(cached0?.summary || {
    records: 0, cash_total: 0, cash_count: 0,
    eft_total: 0, eft_count: 0, total: 0,
    days_with_records: [],
    topups_total: 0, topups_count: 0,
    month_paid_total: 0, month_paid_records: 0,
    remaining: 0,
  });
  const [descriptions, setDescriptions] = useState(cached0?.descriptions || []);
  const [topups, setTopups] = useState(cached0?.topups || []);
  // Managed payment methods (dropdown in the record form): Cash / Bank card /
  // Credit card / Company card / E-transfer seeded; add-rename-delete managed.
  const [methods, setMethods] = useState(cached0?.methods || []);
  const [manageMethodsOpen, setManageMethodsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [previewRecord, setPreviewRecord] = useState(null);

  // Inline approve card (mirrors AdherencePanel).
  const [approveId, setApproveId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [approveBusy, setApproveBusy] = useState(false);

  // Sortable / filterable / searchable view (persisted per session).
  const [view, updateView] = useTableView("consult.expenses.view.v1");
  const fmtDate = useCallback((ymd) => {
    const d = fromYMD(ymd);
    return d ? format(d, "MMM d, yyyy") : (ymd || "");
  }, []);
  const typeLabel = useCallback((t) => {
    const v = t === "eft" ? "bank_card" : t;
    const m = methods.find((x) => x.key === v);
    if (m) return m.label;
    if (v === "company") return "Company";
    if (v === "credit_card") return "Credit card";
    if (v === "bank_card") return "Bank card";
    if (v === "cash") return "Cash";
    return v || "";
  }, [methods]);
  const tableColumns = useMemo(() => ({
    date:        { get: (r) => r.date, displayGet: (r) => fmtDate(r.date), type: "date" },
    description: { get: (r) => r.description || "" },
    type:        {
      get: (r) => (r.type === "eft" ? "bank_card" : r.type) || "",
      displayGet: (r) => typeLabel(r.type),
    },
  }), [fmtDate, typeLabel]);
  const displayRecords = useMemo(
    () => applyTableView(records, view, tableColumns, ["date", "description", "type"]),
    [records, view, tableColumns]
  );
  const uniqDates        = useMemo(() => uniqueValuePairs(records, (r) => r.date, (r) => fmtDate(r.date)), [records, fmtDate]);
  const uniqDescriptions = useMemo(() => uniqueValues(records, (r) => r.description), [records]);
  const uniqTypes        = useMemo(
    () => uniqueValuePairs(
      records,
      (r) => (r.type === "eft" ? "bank_card" : r.type),
      (r) => typeLabel(r.type)
    ),
    [records, typeLabel]
  );

  // True when any table filter is active — totals should reflect the visible rows.
  const viewActive = Boolean(hasActiveFilters(view) || (view.search && view.search.trim()));

  // Compute totals from the currently visible rows so cards match what's on-screen.
  const filteredTotals = useMemo(() => {
    const norm = (t) => (t === "eft" ? "bank_card" : t);
    const acc = {
      records: displayRecords.length,
      cash_total: 0, cash_count: 0,
      bank_total: 0, bank_count: 0,
      cc_total: 0, cc_count: 0,
      company_total: 0, company_count: 0,
      total: 0,
      by_type: {},
    };
    displayRecords.forEach((r) => {
      const t = norm(r.type);
      const isApproved = (r.status || "pending") === "approved";
      const reimbursed = r.amount_reimbursed != null ? Number(r.amount_reimbursed) : null;
      const amt = isApproved && reimbursed != null ? reimbursed : (Number(r.amount) || 0);
      if (t === "bank_card") { acc.bank_total += amt; acc.bank_count += 1; }
      else if (t === "credit_card") { acc.cc_total += amt; acc.cc_count += 1; }
      else if (t === "company") { acc.company_total += amt; acc.company_count += 1; }
      else { acc.cash_total += amt; acc.cash_count += 1; }
      const slot = acc.by_type[t] || (acc.by_type[t] = { total: 0, count: 0 });
      slot.total += amt; slot.count += 1;
      if (t !== "company" && t !== "e_transfer") acc.total += amt;
    });
    ["cash_total", "bank_total", "cc_total", "company_total", "total"].forEach((k) => {
      acc[k] = Math.round(acc[k] * 100) / 100;
    });
    Object.values(acc.by_type).forEach((s) => { s.total = Math.round(s.total * 100) / 100; });
    return acc;
  }, [displayRecords]);

  // Summary values shown on the cards — filtered when a view is active, else full summary.
  const activeSummary = viewActive ? {
    ...summary,
    records: filteredTotals.records,
    cash_total: filteredTotals.cash_total, cash_count: filteredTotals.cash_count,
    bank_total: filteredTotals.bank_total, bank_count: filteredTotals.bank_count,
    cc_total: filteredTotals.cc_total,     cc_count: filteredTotals.cc_count,
    company_total: filteredTotals.company_total, company_count: filteredTotals.company_count,
    total: filteredTotals.total,
    by_type: filteredTotals.by_type,
  } : summary;

  const approvePanelRef = useRef(null);
  const approveRow = records.find((r) => r.id === approveId) || null;

  const openApprove = (r) => {
    setApproveId(r.id);
    // Prefill reimburse amount with submitted amount — admin can override
    // to apply a cap; only the reimbursed amount counts toward outstanding.
    setPayAmount(String(r.amount ?? ""));
    setTimeout(() => {
      approvePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };
  const cancelApprove = () => setApproveId(null);
  const submitApprove = async () => {
    if (!approveId || approveBusy) return;
    const parsed = Number(payAmount);
    if (payAmount === "" || Number.isNaN(parsed) || parsed < 0) {
      toast.error("Enter a valid reimburse amount (0 or more).");
      return;
    }
    setApproveBusy(true);
    try {
      await api.post(`/expenses/records/${approveId}/approve`, { amount_reimbursed: parsed });
      toast.success("Expense approved.");
      setApproveId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve.");
    } finally {
      setApproveBusy(false);
    }
  };

  const load = useCallback(async () => {
    const cacheKey = `${team}|${month}|${day}`;
    const cached = DATA_CACHE.get(cacheKey);
    if (cached) {
      // Show last-loaded data instantly; refresh silently below.
      setRecords(cached.records); setSummary(cached.summary); setDescriptions(cached.descriptions);
      setTopups(cached.topups); setMethods(cached.methods);
    } else {
      setLoading(true);
    }
    try {
      const params = { team, month };
      if (day && day !== "all") params.day = day;
      const [recRes, sumRes, descRes, topupsRes, methodsRes] = await Promise.all([
        api.get("/expenses/records", { params }),
        api.get("/expenses/summary", { params: { team, month, ...(day && day !== "all" ? { day } : {}) } }),
        api.get("/expenses/descriptions"),
        api.get("/expenses/topups", { params: { team, month } }),
        api.get("/expenses/methods"),
      ]);
      const next = {
        records: recRes.data?.items || [],
        summary: sumRes.data || {},
        descriptions: descRes.data?.items || [],
        topups: topupsRes.data?.items || [],
        methods: methodsRes.data?.items || [],
      };
      DATA_CACHE.set(cacheKey, next);
      setRecords(next.records);
      setSummary(next.summary);
      setDescriptions(next.descriptions);
      setTopups(next.topups);
      setMethods(next.methods);
    } catch (e) {
      // 401 handled globally; other errors silent
    } finally {
      setLoading(false);
    }
  }, [team, month, day]);

  useEffect(() => { load(); }, [load, refreshKey]);
  // Changing team keeps the selected day so dates can be compared across teams.
  // Changing month (or leaving/remounting Expenses) resets to all days.
  useEffect(() => { setDay("all"); }, [month]);

  const activeTeamLabel = teams.find((t) => t.key === team)?.label || (lockTeam && team === lockTeam ? (lockTeamLabel || "") : "");
  const activeMonthLabel = months.find((m) => m.key === month)?.label || month;
  const dayOptions = useMemo(() => {
    const days = summary.days_with_records || [];
    return day !== "all" && !days.includes(day) ? [day, ...days] : days;
  }, [summary.days_with_records, day]);

  const openNew = () => {
    setEditing({
      __new: true,
      team,
      date: `${month}-${String(new Date().getDate()).padStart(2, "0")}`
        .replace(/^(\d{4}-\d{2})-(\d{2})$/, (_, mo, d) => {
          const [y, m] = mo.split("-").map(Number);
          const lastDay = new Date(y, m, 0).getDate();
          const dayNum = Math.min(parseInt(d, 10), lastDay);
          return `${mo}-${String(dayNum).padStart(2, "0")}`;
        }),
      description: "",
      type: "cash",
      amount: "",
    });
  };

  const openEdit = (r) => setEditing({ ...r });
  const closeEdit = () => setEditing(null);

  const saveRecord = async () => {
    if (!editing) return;
    const body = {
      team,
      date: editing.date,
      description: (editing.description || "").trim(),
      type: (editing.type || "cash").toLowerCase(),
      amount: parseFloat(editing.amount) || 0,
    };
    if (!body.date) { toast.error("Date is required"); return; }
    if (!body.amount || body.amount <= 0) { toast.error("Amount must be greater than 0"); return; }
    if (!body.description) { toast.error("Description is required"); return; }
    // Receipt is mandatory — either a freshly picked file or one already on file.
    const hasFile = !!editing._pendingFile || !!editing.receipt_filename;
    if (!hasFile) { toast.error("Please upload a receipt before saving."); return; }
    try {
      let savedId = editing.id;
      if (editing.__new) {
        const res = await api.post("/expenses/records", body);
        savedId = res.data?.id || savedId;
        toast.success("Record added");
      } else {
        await api.patch(`/expenses/records/${editing.id}`, {
          date: body.date,
          description: body.description,
          type: body.type,
          amount: body.amount,
        });
        toast.success("Record updated");
      }
      // If a fresh file was picked in the dialog, upload it now.
      if (editing._pendingFile && savedId) {
        const fd = new FormData();
        fd.append("file", editing._pendingFile);
        try {
          await api.post(`/expenses/records/${savedId}/receipt`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Receipt uploaded");
        } catch (uerr) {
          toast.error(uerr?.response?.data?.detail || "Receipt upload failed");
        }
      }
      closeEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save record");
    }
  };

  const deleteRecord = async () => {
    if (!editing || editing.__new) { closeEdit(); return; }
    if (!window.confirm("Delete this expense record?")) return;
    try {
      await api.delete(`/expenses/records/${editing.id}`);
      toast.success("Record deleted");
      closeEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete record");
    }
  };

  const removeReceipt = async (rid) => {
    if (!window.confirm("Remove uploaded receipt for this record?")) return;
    try {
      await api.delete(`/expenses/records/${rid}/receipt`);
      toast.success("Receipt removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove receipt");
    }
  };

  return (
    <div data-testid="expenses-panel" style={{
      background: "#fff",
      borderRadius: 14,
      border: `1px solid ${BORDER}`,
      padding: 20,
    }}>
      <style>{`
        .exp-team-month {
          display: flex; align-items: stretch; border: 1px solid #e2e8f0;
          background: #fff; border-radius: 6px; overflow: hidden; width: 100%;
        }
        .exp-team-btn {
          flex: 1; height: 36px; padding: 0 12px; font-size: 12px; font-weight: 600;
          color: #64748b; background: #fff; border: 0;
          border-right: 1px solid #e2e8f0; cursor: pointer;
        }
        .exp-team-btn.active { background: ${NAVY}; color: #fff; }
        .exp-day-select, .exp-month-select {
          flex: 1; height: 36px; padding: 0 30px 0 12px; font-size: 12px; font-weight: 600;
          color: #fff; background: ${NAVY}; border: 0; text-align: center;
          -webkit-appearance: none; appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>");
          background-repeat: no-repeat; background-position: right 8px center;
          cursor: pointer;
        }
        .exp-day-select { border-right: 1px solid rgba(255,255,255,0.20); }
        @media (max-width: 640px) {
          .exp-team-month { flex-wrap: wrap; }
          .exp-team-btn { flex: 1 1 33%; border-bottom: 1px solid #e2e8f0; }
          /* Row 1 = Team 1 | Team 2 | Team 3, Row 2 = HQ full-width bar */
          .exp-team-btn:nth-of-type(3) { border-right: 0; }
          .exp-team-btn.hq { flex: 1 1 100%; border-right: 0; }
          .exp-team-btn:last-of-type { border-right: 0; }
          .exp-day-select, .exp-month-select { flex: 1 1 50%; border-top: 0; }
          /* staff view: single locked team — everything fits on one line */
          .exp-team-month.locked { flex-wrap: nowrap; }
          .exp-team-month.locked .exp-team-btn { flex: 0 0 auto; border-bottom: 0; }
          .exp-team-month.locked .exp-day-select,
          .exp-team-month.locked .exp-month-select { flex: 1 1 0; min-width: 0; padding: 0 24px 0 8px; }
        }
        .exp-tbl-scroll { overflow-x: auto; margin: 0 -6px; }
        .exp-tbl { width: 100%; min-width: 780px; border-collapse: collapse; font-size: 13px; }
        .exp-tbl th { text-align: left; font-size: 10px; font-weight: 700;
          color: ${MUTED}; text-transform: uppercase; letter-spacing: 0.14em;
          padding: 8px 10px; border-bottom: 1px solid ${BORDER}; white-space: nowrap; }
        .exp-tbl td { padding: 10px; border-bottom: 1px solid ${BORDER}; color: ${TEXT};
          vertical-align: middle; white-space: nowrap; }
        .exp-tbl td.description { white-space: normal; min-width: 180px; }
        .exp-tbl tr:hover td { background: #FAFAF7; }
        .exp-icon-btn { background: none; border: none; cursor: pointer;
          color: ${MUTED}; padding: 4px; margin-left: 2px; }
        .exp-icon-btn:hover { color: ${DARK_GREEN}; }
        .exp-icon-btn.danger:hover { color: #B4351B; }
        .exp-pill { display: inline-block; padding: 2px 8px; border-radius: 999px;
          font-size: 11px; font-weight: 600; }
        .exp-pill-cash { background: rgba(46,125,91,0.10); color: ${TAG_CASH}; }
        .exp-pill-bank { background: rgba(74,111,176,0.10); color: ${TAG_BANK}; }
        .exp-pill-cc { background: rgba(124,74,159,0.12); color: ${TAG_CC}; }
        .exp-pill-company { background: rgba(166,101,0,0.12); color: ${TAG_COMPANY}; }
        .exp-pill-custom { background: rgba(15,59,46,0.10); color: ${DARK_GREEN}; }
        .exp-file-badge { display: inline-flex; align-items: center; gap: 4px;
          padding: 2px 8px; border-radius: 999px; background: rgba(15,59,46,0.08);
          color: ${DARK_GREEN}; font-size: 11px; font-weight: 600;
          cursor: pointer; border: 1px solid rgba(15,59,46,0.15); }
        .exp-file-badge:hover { background: rgba(15,59,46,0.14); }
        /* Header row (title + balance pill + Add record) — ALWAYS one line,
           on every team, regardless of how long the balance amount is.
           Tightens slightly on narrow phones instead of wrapping. */
        .exp-head-row { display: flex; align-items: center; justify-content: space-between;
          gap: 8px; margin-bottom: 14px; flex-wrap: nowrap; }
        .exp-head-actions { display: flex; gap: 6px; flex-shrink: 1; flex-wrap: nowrap; min-width: 0; }
        .exp-balance-pill { padding: 8px 9px !important; font-size: 12.5px !important; }
        .exp-add-btn { padding: 8px 12px !important; font-size: 12.5px !important; white-space: nowrap; }
        /* The balance pill must NEVER resize — same padding/font at every
           viewport, so it looks identical on Expenses and Rewards always. */
      `}</style>

      <div className="exp-head-row">
        <h2 style={{
          fontFamily: "'Work Sans', system-ui, sans-serif",
          fontSize: 18, fontWeight: 700, margin: 0, flexShrink: 0,
        }}>
          Expenses
        </h2>
        <div className="exp-head-actions">
          <button
            type="button"
            data-testid="expenses-balance-btn"
            className="exp-balance-pill"
            onClick={() => setBalanceOpen(true)}
            title="Cash balance for this team + month"
            style={{
              background: "#fff",
              color: (summary.remaining || 0) < 0 ? "#B4351B" : DARK_GREEN,
              border: `1px solid ${(summary.remaining || 0) < 0 ? "#E9BFB1" : "rgba(15,59,46,0.25)"}`,
              borderRadius: 8,
              fontWeight: 700,
              minWidth: 62, justifyContent: "center",
              display: "inline-flex", alignItems: "center", gap: 5,
              cursor: "pointer", whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span aria-hidden className="exp-balance-emoji" style={{ fontSize: 13 }}>💰</span>
            {money(summary.remaining || 0)}
          </button>
          <Button
            data-testid="expenses-add-btn"
            className="exp-add-btn"
            onClick={openNew}
            style={{
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
            }}
          >
            <Plus style={{ width: 15, height: 15 }} strokeWidth={2.5} />
            Add record
          </Button>
        </div>
      </div>

      <div className={`exp-team-month${lockTeam ? " locked" : ""}`} data-testid="expenses-team-month" style={{ marginBottom: 14 }}>
        {(lockTeam
          ? (teams.some((t) => t.key === lockTeam)
              ? teams.filter((t) => t.key === lockTeam)
              // Registry fetch hasn't landed yet — render the locked pill
              // immediately so the strip never gains a segment late.
              : [{ key: lockTeam, label: lockTeamLabel || lockTeam }])
          : teams
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`expenses-team-${t.key}`}
            onClick={() => setTeam(t.key)}
            className={`exp-team-btn${t.key === team ? " active" : ""}${t.key === "hq" ? " hq" : ""}`}
          >
            {t.label}
          </button>
        ))}
        <select
          data-testid="expenses-day-select"
          className="exp-day-select"
          value={day}
          onChange={(e) => setDay(e.target.value)}
        >
          <option value="all" style={{ color: "#000" }}>All days</option>
          {dayOptions.map((d) => (
            <option key={d} value={d} style={{ color: "#000" }}>
              {fromYMD(d) ? format(fromYMD(d), "MMM d") : d}
            </option>
          ))}
        </select>
        <select
          data-testid="expenses-month-select"
          className="exp-month-select"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {months.map((m) => (
            <option key={m.key} value={m.key} style={{ color: "#000" }}>{m.label}</option>
          ))}
        </select>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 10, marginBottom: 14,
      }}>
        <TotalCard testId="expenses-total-records"
          label="Records" value={activeSummary.records || 0}
          sub={viewActive ? "Filtered view" : (day === "all" ? "This month" : "Selected day")}
        />
        {/* Grand total across ALL payment types combined (cash + bank card +
            credit card + company card + e-transfer + customs) — for quick
            reference without adding the cards up. */}
        {(() => {
          const byType = activeSummary.by_type || {};
          const keys = Object.keys(byType);
          const combined = keys.length
            ? keys.reduce((s, k) => s + (byType[k]?.total || 0), 0)
            : (activeSummary.total || 0);
          const combinedCount = keys.length
            ? keys.reduce((s, k) => s + (byType[k]?.count || 0), 0)
            : (activeSummary.records || 0);
          return (
            <TotalCard testId="expenses-total-all"
              label="Total"
              value={money(combined)}
              sub={`All payment types · ${combinedCount} record${combinedCount === 1 ? "" : "s"}`}
              strong
            />
          );
        })()}
        {/* One card per managed payment method (Cash / Bank card / Credit
            card / Company card / E-transfer + any custom ones). Totals come
            from the dynamic by_type map so renames and new options just work. */}
        {(methods.length ? methods : [
          { key: "cash", label: "Cash" },
          { key: "bank_card", label: "Bank card" },
          { key: "credit_card", label: "Credit card" },
          { key: "company", label: "Company card" },
        ]).map((m) => {
          const slot = (activeSummary.by_type || {})[m.key] || { total: 0, count: 0 };
          return (
            <TotalCard key={m.key} testId={`expenses-total-${m.key}`}
              label={m.label}
              value={money(slot.total || 0)}
              sub={`${slot.count || 0} record${(slot.count || 0) === 1 ? "" : "s"}${(m.key === "company" || m.key === "e_transfer") && !viewActive ? " · auto-paid" : ""}`}
            />
          );
        })}
        <TotalCard testId="expenses-total-paid"
          label="Owing"
          value={money(viewActive ? (activeSummary.total || 0) : Math.round(((summary.total || 0) - (summary.topups_total || 0)) * 100) / 100)}
          sub={viewActive
            ? "Filtered view"
            : `${activeTeamLabel} · ${activeMonthLabel}${(summary.topups_total || 0) > 0 ? " · after top-ups" : ""}`}
          strong
        />
      </div>

      {loading && records.length === 0 ? (
        <div style={{ color: MUTED, padding: 30, textAlign: "center" }}>Loading…</div>
      ) : records.length === 0 ? (
        <div data-testid="expenses-empty" style={{
          border: `1px dashed ${BORDER}`, borderRadius: 10, padding: 32,
          textAlign: "center", color: MUTED,
        }}>
          No expense records for {activeTeamLabel} in {activeMonthLabel}
          {day !== "all" ? ` on ${format(fromYMD(day) || new Date(), "MMM d")}` : ""}.
          <div style={{ marginTop: 8, fontSize: 12 }}>
            Tap <b>+ Add record</b> to log an expense.
          </div>
        </div>
      ) : (
        <div className="exp-tbl-scroll">
          <div style={{
            display: "flex", alignItems: "center", gap: 8, padding: "0 6px 8px",
            flexWrap: "wrap",
          }}>
            <input
              data-testid="expenses-search"
              type="text"
              value={view.search || ""}
              onChange={(e) => updateView({ search: e.target.value })}
              placeholder="Search date, description, type…"
              style={{
                flex: 1, minWidth: 200, height: 34,
                padding: "0 10px", fontSize: 13,
                border: `1px solid ${BORDER}`, borderRadius: 8,
                background: "#fff", color: TEXT, boxSizing: "border-box",
              }}
            />
            {(view.sortKey || hasActiveFilters(view) || view.search) && (
              <button
                type="button"
                data-testid="expenses-view-clear"
                onClick={() => updateView({ sortKey: null, sortDir: null, filters: {}, search: "" })}
                style={{
                  background: "rgba(180,53,27,0.08)", border: `1px solid #E9BFB1`,
                  borderRadius: 8, padding: "6px 12px",
                  fontSize: 12, fontWeight: 700, color: "#B4351B", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <XIcon style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                Clear all filters
              </button>
            )}
            <div data-testid="expenses-shown-count" style={{ fontSize: 12, color: MUTED }}>
              {displayRecords.length} of {records.length}
            </div>
          </div>
          <table className="exp-tbl" data-testid="expenses-table">
            <thead>
              <tr>
                <TableColumnHeader columnKey="date" label="Date" type="date"
                  values={uniqDates} view={view} onChange={updateView}
                  testId="expenses" />
                <TableColumnHeader columnKey="description" label="Description"
                  values={uniqDescriptions} view={view} onChange={updateView}
                  testId="expenses" />
                <TableColumnHeader columnKey="type" label="Type"
                  values={uniqTypes} view={view} onChange={updateView}
                  testId="expenses" />
                <th>Receipt</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayRecords.length === 0 && (
                <tr data-testid="expenses-no-matches">
                  <td colSpan={7} style={{ padding: 24, textAlign: "center", color: MUTED }}>
                    No records match your search or filter.
                  </td>
                </tr>
              )}
              {displayRecords.map((r) => {
                const isApproved = (r.status || "pending") === "approved";
                const submitted = Number(r.amount) || 0;
                const reimbursed = r.amount_reimbursed != null ? (Number(r.amount_reimbursed) || 0) : null;
                const variance = isApproved && reimbursed != null ? submitted - reimbursed : 0;
                return (
                  <tr key={r.id} data-testid={`expenses-row-${r.id}`}>
                    <td>{fromYMD(r.date) ? format(fromYMD(r.date), "MMM d, yyyy") : r.date}</td>
                    <td className="description">{r.description || "—"}</td>
                    <td>
                      {(() => {
                        const t = r.type === "eft" ? "bank_card" : r.type;
                        const PILL_CLASS = {
                          company: "exp-pill-company",
                          credit_card: "exp-pill-cc",
                          bank_card: "exp-pill-bank",
                          cash: "exp-pill-cash",
                        };
                        const cls = PILL_CLASS[t] || "exp-pill-custom";
                        return (
                          <span
                            className={`exp-pill ${cls}`}
                            title={t === "company" ? "Paid by company credit card" : t === "e_transfer" ? "Paid directly by e-transfer" : undefined}
                          >
                            {typeLabel(r.type)}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {r.receipt_filename ? (
                        <button
                          type="button"
                          className="exp-file-badge"
                          data-testid={`expenses-preview-${r.id}`}
                          onClick={() => setPreviewRecord(r)}
                          title={`Preview ${r.receipt_filename}`}
                        >
                          <FileText style={{ width: 12, height: 12 }} strokeWidth={2} />
                          View
                        </button>
                      ) : (
                        <span style={{ color: MUTED, fontSize: 11 }}>—</span>
                      )}
                    </td>
                    <td>
                      {isApproved ? (
                        <span className="exp-pill" style={{ background: "rgba(46,125,91,0.14)", color: TAG_CASH }}>
                          Approved
                        </span>
                      ) : (
                        <span className="exp-pill" style={{ background: "rgba(229,138,46,0.10)", color: "#E58A2E" }}>
                          Pending
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {isApproved && reimbursed != null ? (
                        <div>
                          <div style={{ fontWeight: 700, color: DARK_GREEN }}>{money(reimbursed)}</div>
                          {variance > 0.005 && (
                            <div style={{ fontSize: 10, color: MUTED, textDecoration: "line-through" }}>
                              {money(submitted)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontWeight: 600 }}>{money(submitted)}</div>
                      )}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {!isApproved && (
                        <button
                          type="button"
                          className="exp-icon-btn"
                          data-testid={`expenses-approve-${r.id}`}
                          onClick={() => openApprove(r)}
                          title="Approve"
                          style={{ color: DARK_GREEN }}
                        >
                          <ShieldCheck style={{ width: 16, height: 16 }} strokeWidth={2} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="exp-icon-btn"
                        data-testid={`expenses-edit-${r.id}`}
                        onClick={() => openEdit(r)}
                        title={isApproved ? "Edit (admin)" : "Edit"}
                      >
                        <Pencil style={{ width: 15, height: 15 }} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Inline approve panel */}
      {approveRow && (
        <div ref={approvePanelRef} data-testid="expenses-approve-panel" style={{
          marginTop: 16, padding: 16,
          background: "#F4F9F6", border: `1px solid ${BORDER}`, borderRadius: 12,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".14em",
            textTransform: "uppercase", color: LABEL_GREEN }}>
            Approve · {approveRow.description || "expense"}
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
            Submitted <b style={{ color: TEXT }}>{money(approveRow.amount)}</b> · {fromYMD(approveRow.date) ? format(fromYMD(approveRow.date), "MMM d, yyyy") : approveRow.date}
          </div>
          <div style={{ marginTop: 12, maxWidth: 320 }}>
            <p style={{ ...labelStyle, marginBottom: 6 }}>Reimburse amount</p>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute", left: 12, top: "50%",
                transform: "translateY(-50%)", color: MUTED,
                fontWeight: 600, fontSize: 14,
              }}>$</span>
              <input
                type="text"
                inputMode="decimal"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                data-testid="expenses-approve-amount"
                style={{ ...inputStyle, paddingLeft: 26, width: "100%" }}
                placeholder="0.00"
              />
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 6, lineHeight: 1.45 }}>
              Enter the capped rate you&apos;ll reimburse. The variance
              (submitted − reimbursed) is <b>not</b> deducted from the team&apos;s top-up.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button type="button" onClick={cancelApprove} data-testid="expenses-approve-cancel"
              style={{
                flex: "0 0 auto", background: "#fff", color: TEXT,
                border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}>Cancel</button>
            <button type="button" onClick={submitApprove} disabled={approveBusy}
              data-testid="expenses-approve-confirm"
              style={{
                flex: 1, background: DARK_GREEN, color: "#fff", border: 0,
                borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 700,
                cursor: approveBusy ? "default" : "pointer",
                opacity: approveBusy ? 0.6 : 1,
              }}>
              {approveBusy ? "Approving…" : "Confirm approval"}
            </button>
          </div>
        </div>
      )}

      {/* keep old spacer for original close of the conditional block */}
      <RecordDialog
        editing={editing}
        setEditing={setEditing}
        onClose={closeEdit}
        onSave={saveRecord}
        onDelete={deleteRecord}
        onRemoveReceipt={removeReceipt}
        descriptions={descriptions}
        onOpenManageDescriptions={() => setManageOpen(true)}
        methods={methods}
        onOpenManageMethods={() => setManageMethodsOpen(true)}
        onPreview={(rec) => setPreviewRecord(rec)}
        activeTeamLabel={activeTeamLabel}
        activeMonthLabel={activeMonthLabel}
      />

      <ManageDescriptionsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        descriptions={descriptions}
        onChanged={() => load()}
      />

      <ManageMethodsDialog
        open={manageMethodsOpen}
        onOpenChange={setManageMethodsOpen}
        methods={methods}
        onChanged={() => load()}
      />

      <BalanceDialog
        open={balanceOpen}
        onOpenChange={setBalanceOpen}
        team={team}
        month={month}
        activeTeamLabel={activeTeamLabel}
        activeMonthLabel={activeMonthLabel}
        topups={topups}
        summary={summary}
        onChanged={() => load()}
      />

      <ReceiptPreviewDialog
        record={previewRecord}
        onClose={() => setPreviewRecord(null)}
      />
    </div>
  );
}

// ============================================================================
function TotalCard({ label, value, sub, strong = false, testId }) {
  return (
    <div data-testid={testId} style={{
      border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px",
      background: "#F9F8F6",
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
        textTransform: "uppercase", color: LABEL_GREEN,
      }}>{label}</div>
      <div style={{
        fontSize: 20, fontWeight: 700, marginTop: 4,
        color: strong ? DARK_GREEN : TEXT,
      }}>{value}</div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

// ============================================================================
function RecordDialog({
  editing, setEditing, onClose, onSave, onDelete,
  onRemoveReceipt, descriptions, onOpenManageDescriptions,
  methods, onOpenManageMethods,
  onPreview, activeTeamLabel, activeMonthLabel,
}) {
  const fileRef = useRef(null);
  useBackClosesDialog(!!editing, onClose, "record");
  if (!editing) return null;
  const isNew = !!editing.__new;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setEditing((cur) => ({ ...(cur || {}), _pendingFile: f, _pendingFileName: f.name }));
  };

  const hasReceipt = !!editing.receipt_filename;
  const hasPending = !!editing._pendingFile;

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="expenses-record-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add expense" : "Edit expense"}</DialogTitle>
          <DialogDescription>{activeTeamLabel} · {activeMonthLabel}</DialogDescription>
        </DialogHeader>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <p style={fieldLabelStyle}>Date</p>
            <DatePickerButton
              testId="expenses-field-date"
              value={editing.date || ""}
              onChange={(v) => setEditing({ ...editing, date: v })}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
              <p style={{ ...fieldLabelStyle, marginBottom: 0 }}>Type</p>
              <button
                type="button"
                data-testid="expenses-manage-methods-link"
                onClick={onOpenManageMethods}
                style={{
                  background: "none", border: "none", padding: 0, paddingRight: 4,
                  fontSize: 12, fontWeight: 600, color: DARK_GREEN,
                  textDecoration: "underline", cursor: "pointer",
                }}
              >
                Manage
              </button>
            </div>
            <select
              data-testid="expenses-field-type"
              value={editing.type || "cash"}
              onChange={(e) => setEditing({ ...editing, type: e.target.value })}
              style={{
                ...inputStyle,
                appearance: "none",
                WebkitAppearance: "none",
                paddingRight: 30,
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236B7A73'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
              }}
            >
              {(methods.length ? methods : [
                { key: "cash", label: "Cash" },
                { key: "bank_card", label: "Bank card" },
                { key: "credit_card", label: "Credit card" },
                { key: "company", label: "Company (credit card)" },
              ]).map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
              <p style={{ ...fieldLabelStyle, marginBottom: 0 }}>Description</p>
              <button
                type="button"
                data-testid="expenses-manage-descriptions-link"
                onClick={onOpenManageDescriptions}
                style={{
                  background: "none", border: 0, padding: 0, paddingRight: 4,
                  color: DARK_GREEN, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", textDecoration: "underline",
                }}
              >
                Manage
              </button>
            </div>
            <select
              data-testid="expenses-field-description"
              value={editing.description || ""}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              style={{
                ...inputStyle,
                appearance: "none",
                WebkitAppearance: "none",
                paddingRight: 30,
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236B7A73'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
              }}
            >
              <option value="">Select description…</option>
              {descriptions.map((r) => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <p style={fieldLabelStyle}>Amount</p>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute", left: 12, top: "50%",
                transform: "translateY(-50%)", color: MUTED,
                fontWeight: 600, fontSize: 14,
              }}>$</span>
              <input
                data-testid="expenses-field-amount"
                type="text"
                inputMode="decimal"
                value={editing.amount ?? ""}
                onChange={(e) => setEditing({ ...editing, amount: e.target.value.replace(/[^0-9.]/g, "") })}
                style={{ ...inputStyle, paddingLeft: 26 }}
                placeholder="40.00"
              />
            </div>
          </div>

          {/* Receipt upload (required) */}
          <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <p style={labelStyle}>Receipt <span style={{ color: "#B4351B", fontWeight: 800 }}>*</span></p>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,image/*"
              onChange={pickFile}
              style={{ display: "none" }}
              data-testid="expenses-field-file-input"
            />
            <div style={{
              display: "flex", gap: 6, alignItems: "stretch",
              flexWrap: "nowrap",
            }}>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="expenses-field-upload-btn"
                style={{
                  height: 38, minWidth: 0, flex: "1 1 auto", cursor: "pointer",
                  padding: "0 10px",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
                  background: "rgba(15,59,46,0.05)", color: DARK_GREEN,
                  border: `1px solid rgba(15,59,46,0.25)`, borderRadius: 8,
                  fontWeight: 600, fontSize: 12, fontFamily: "inherit",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
              >
                <Upload style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {hasPending ? "Change" : hasReceipt ? "Replace" : "Upload"}
                </span>
              </button>
              {hasReceipt && !isNew && (
                <button
                  type="button"
                  onClick={() => onPreview && onPreview(editing)}
                  data-testid="expenses-field-preview-btn"
                  style={{
                    height: 38, flex: "1 1 auto", cursor: "pointer",
                    padding: "0 10px", minWidth: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
                    background: "#fff", color: DARK_GREEN,
                    border: `1px solid rgba(15,59,46,0.25)`, borderRadius: 8,
                    fontWeight: 600, fontSize: 12, fontFamily: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Eye style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2} />
                  Preview
                </button>
              )}
              {(hasReceipt || hasPending) && (
                <button
                  type="button"
                  onClick={() => {
                    if (hasPending) {
                      setEditing((cur) => ({ ...(cur || {}), _pendingFile: null, _pendingFileName: "" }));
                      if (fileRef.current) fileRef.current.value = "";
                    } else if (hasReceipt && !isNew) {
                      onRemoveReceipt(editing.id);
                    }
                  }}
                  data-testid="expenses-field-clear-file-btn"
                  title={hasPending ? "Discard picked file" : "Remove uploaded receipt"}
                  style={{
                    height: 38, width: 38, flex: "0 0 38px", cursor: "pointer",
                    padding: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: "#fff", color: "#B4351B",
                    border: `1px solid #E9BFB1`, borderRadius: 8,
                  }}
                >
                  <XIcon style={{ width: 14, height: 14 }} strokeWidth={2} />
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
              {hasPending
                ? <>Ready to upload: <b>{editing._pendingFileName}</b></>
                : hasReceipt
                  ? <>On file: <b>{editing.receipt_filename}</b></>
                  : "PDF or image (JPG / PNG / WebP), 10 MB max."}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <button
            type="button"
            data-testid="expenses-save-btn"
            onClick={onSave}
            style={{
              flex: 1, background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {isNew ? "Save record" : "Update record"}
          </button>
          {!isNew && (
            <button
              type="button"
              data-testid="expenses-delete-btn"
              onClick={onDelete}
              style={{
                background: "#fff", color: "#B4351B",
                border: "1px solid #E9BFB1", borderRadius: 8,
                padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              Delete
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function ManageDescriptionsDialog({ open, onOpenChange, descriptions, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  useBackClosesDialog(open, () => onOpenChange(false), "manage");

  useEffect(() => {
    if (open) setRows(descriptions.map((r) => ({ ...r, _draft: r.name })));
  }, [open, descriptions]);

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.name) return;
    setBusy(true);
    try {
      await api.patch(`/expenses/descriptions/${r.id}`, { name: r._draft.trim() });
      toast.success("Description renamed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete description "${r.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/expenses/descriptions/${r.id}`);
      toast.success("Description deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/expenses/descriptions", { name });
      setNewName("");
      toast.success("Description added");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expenses-manage-descriptions-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage descriptions</DialogTitle>
          <DialogDescription>
            Add, rename or delete descriptions in the dropdown. Shared across all teams (Team 1, Team 2, HQ).
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {rows.length === 0 && (
            <div style={{ color: MUTED, fontSize: 13, padding: 12, textAlign: "center" }}>
              No descriptions yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div
              key={r.id}
              data-testid={`expenses-desc-row-${r.id}`}
              style={{
                display: "flex", gap: 8, alignItems: "center",
                padding: "8px 10px", border: `1px solid ${BORDER}`,
                borderRadius: 8,
              }}
            >
              <input
                value={r._draft}
                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, _draft: e.target.value } : x))}
                onBlur={() => saveRow(r)}
                data-testid={`expenses-desc-input-${r.id}`}
                style={{ flex: 1, border: 0, padding: 0, background: "transparent", fontSize: 14, color: TEXT, outline: "none" }}
              />
              {r._draft.trim() && r._draft.trim() !== r.name && (
                <button
                  type="button"
                  className="exp-icon-btn"
                  onClick={() => saveRow(r)}
                  data-testid={`expenses-desc-save-${r.id}`}
                  title="Save"
                >
                  <Check style={{ width: 15, height: 15, color: TAG_CASH }} strokeWidth={2.5} />
                </button>
              )}
              <button
                type="button"
                className="exp-icon-btn danger"
                onClick={() => deleteRow(r)}
                data-testid={`expenses-desc-delete-${r.id}`}
                title="Delete"
              >
                <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="expenses-new-desc-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
            placeholder="Add new description…"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            data-testid="expenses-new-desc-add"
            onClick={addRow}
            disabled={!newName.trim() || busy}
            style={{
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "0 16px", fontSize: 13, fontWeight: 600,
              cursor: !newName.trim() || busy ? "default" : "pointer",
              opacity: !newName.trim() || busy ? 0.6 : 1,
            }}
          >
            Add
          </button>
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="expenses-manage-desc-done"
            onClick={() => onOpenChange(false)}
            style={{
              width: "100%", background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Manage payment methods (the Type dropdown): add / rename / delete.
// Core methods (Cash, Bank card, Credit card, Company card) power the
// summary cards and top-up math — they can be renamed but not deleted;
// the backend also blocks deleting any option that records are using.
function ManageMethodsDialog({ open, onOpenChange, methods, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  useBackClosesDialog(open, () => onOpenChange(false), "manage-methods");

  useEffect(() => {
    if (open) setRows((methods || []).map((r) => ({ ...r, _draft: r.label })));
  }, [open, methods]);

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.label) return;
    setBusy(true);
    try {
      await api.patch(`/expenses/methods/${r.id}`, { label: r._draft.trim() });
      toast.success("Payment method renamed — existing records follow the new name");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete payment method "${r.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/expenses/methods/${r.id}`);
      toast.success("Payment method deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      await api.post("/expenses/methods", { label });
      setNewLabel("");
      toast.success("Payment method added");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expenses-manage-methods-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage payment methods</DialogTitle>
          <DialogDescription>
            Add, rename or delete options in the Type dropdown. Renames update every existing record automatically.
            Core methods (Cash, Bank card, Credit card, Company card) can be renamed but not deleted.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {rows.length === 0 && (
            <div style={{ color: MUTED, fontSize: 13, padding: 12, textAlign: "center" }}>
              No payment methods yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => {
            const isCore = ["cash", "bank_card", "credit_card", "company"].includes(r.key);
            return (
              <div
                key={r.id}
                data-testid={`expenses-method-row-${r.key}`}
                style={{
                  display: "flex", gap: 8, alignItems: "center",
                  padding: "8px 10px", border: `1px solid ${BORDER}`,
                  borderRadius: 8,
                }}
              >
                <input
                  value={r._draft}
                  onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, _draft: e.target.value } : x))}
                  onBlur={() => saveRow(r)}
                  data-testid={`expenses-method-input-${r.key}`}
                  style={{ flex: 1, border: 0, padding: 0, background: "transparent", fontSize: 14, color: TEXT, outline: "none" }}
                />
                {r._draft.trim() && r._draft.trim() !== r.label && (
                  <button
                    type="button"
                    className="exp-icon-btn"
                    onClick={() => saveRow(r)}
                    data-testid={`expenses-method-save-${r.key}`}
                    title="Save"
                  >
                    <Check style={{ width: 15, height: 15, color: TAG_CASH }} strokeWidth={2.5} />
                  </button>
                )}
                <button
                  type="button"
                  className="exp-icon-btn danger"
                  onClick={() => deleteRow(r)}
                  data-testid={`expenses-method-delete-${r.key}`}
                  title={isCore ? "Core method — can be renamed but not deleted" : "Delete"}
                  style={isCore ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
                >
                  <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="expenses-new-method-input"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
            placeholder="Add new payment method…"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            data-testid="expenses-new-method-add"
            onClick={addRow}
            disabled={!newLabel.trim() || busy}
            style={{
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "0 16px", fontSize: 13, fontWeight: 600,
              cursor: !newLabel.trim() || busy ? "default" : "pointer",
              opacity: !newLabel.trim() || busy ? 0.6 : 1,
            }}
          >
            Add
          </button>
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="expenses-manage-methods-done"
            onClick={() => onOpenChange(false)}
            style={{
              width: "100%", background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
function BalanceDialog({
  open, onOpenChange, team, month, activeTeamLabel, activeMonthLabel,
  topups, summary, onChanged,
}) {
  const todayYMD = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  // The date is always the real day the cash was added — the top-up still
  // counts toward the selected ledger month even when it's a past month.
  const defaultDate = () => todayYMD();

  const [date, setDate] = useState(defaultDate());
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  useBackClosesDialog(open, () => onOpenChange(false), "balance");

  useEffect(() => {
    if (open) {
      setDate(defaultDate());
      setAmount("");
    }
  }, [open, month]);

  const addTopup = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
    if (!date) { toast.error("Date is required"); return; }
    setBusy(true);
    try {
      await api.post("/expenses/topups", { team, date, amount: amt, month });
      toast.success("Top-up added");
      setAmount("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add top-up");
    } finally {
      setBusy(false);
    }
  };

  const deleteTopup = async (t) => {
    if (!window.confirm(`Delete top-up of ${money(t.amount)} on ${format(fromYMD(t.date) || new Date(), "MMM d, yyyy")}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/expenses/topups/${t.id}`);
      toast.success("Top-up deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete top-up");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expenses-balance-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cash balance</DialogTitle>
          <DialogDescription>
            <b>{activeTeamLabel}</b> · {activeMonthLabel}
            <div style={{ fontSize: 11, marginTop: 4 }}>
              Each team keeps its own cash budget — this is {activeTeamLabel}&apos;s {activeMonthLabel} top-up + expense ledger.
            </div>
          </DialogDescription>
        </DialogHeader>

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8,
          marginBottom: 8,
        }}>
          <MiniTile testId="expenses-balance-cash-in"
            label="Cash in" value={money(summary.topups_total || 0)}
            sub={`${summary.topups_count || 0} top-up${(summary.topups_count || 0) === 1 ? "" : "s"}`}
          />
          <MiniTile testId="expenses-balance-paid-out"
            label="Spent" value={money(summary.month_paid_total || 0)}
            sub={`${summary.month_paid_records || 0} record${(summary.month_paid_records || 0) === 1 ? "" : "s"}`}
          />
          <MiniTile testId="expenses-balance-remaining"
            label="Remaining" value={money(summary.remaining || 0)}
            sub="Available" strong
            danger={(summary.remaining || 0) < 0}
          />
        </div>
        {(summary.variance_total || 0) > 0 && (
          <div style={{
            padding: "8px 12px", borderRadius: 8, marginBottom: 16,
            background: "rgba(46,125,91,0.08)", border: "1px solid rgba(46,125,91,0.25)",
            fontSize: 12, color: DARK_GREEN,
          }} data-testid="expenses-balance-variance">
            <b>Cap savings:</b> {money(summary.variance_total)} not charged to the top-up
            (submitted amounts exceeded reimbursed caps).
          </div>
        )}
        {(summary.variance_total || 0) === 0 && (
          <div style={{ marginBottom: 16 }} />
        )}

        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 14, marginBottom: 14 }}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Add top-up</div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: -4, marginBottom: 8 }}>
            Counts toward {activeMonthLabel} — the date stays the real day you add it.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ ...labelStyle, fontSize: 10 }}>Date</p>
              <DatePickerButton
                testId="expenses-topup-date"
                value={date}
                onChange={(v) => setDate(v)}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ ...labelStyle, fontSize: 10 }}>Amount</p>
              <div style={{ position: "relative" }}>
                <span style={{
                  position: "absolute", left: 12, top: "50%",
                  transform: "translateY(-50%)", color: MUTED,
                  fontWeight: 600, fontSize: 14,
                }}>$</span>
                <input
                  data-testid="expenses-topup-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="200"
                  style={{ ...inputStyle, paddingLeft: 26 }}
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="expenses-topup-add"
            onClick={addTopup}
            disabled={busy || !amount}
            style={{
              width: "100%", marginTop: 10,
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: busy || !amount ? "default" : "pointer",
              opacity: busy || !amount ? 0.6 : 1,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <Plus style={{ width: 15, height: 15 }} strokeWidth={2.5} />
            Add top-up
          </button>
        </div>

        <div style={{ ...labelStyle, marginBottom: 8 }}>This month&apos;s top-ups</div>
        {topups.length === 0 ? (
          <div style={{
            border: `1px dashed ${BORDER}`, borderRadius: 8, padding: 16,
            textAlign: "center", color: MUTED, fontSize: 13,
          }}>
            No top-ups yet for {activeMonthLabel}.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topups.map((t) => (
              <div
                key={t.id}
                data-testid={`expenses-topup-row-${t.id}`}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: 8,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {fromYMD(t.date) ? format(fromYMD(t.date), "MMM d, yyyy") : t.date}
                  </div>
                  <div style={{ fontSize: 11, color: MUTED }}>Added top-up</div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{
                    fontWeight: 700, color: DARK_GREEN,
                    fontVariantNumeric: "tabular-nums",
                  }}>{money(t.amount)}</div>
                  <button
                    type="button"
                    className="exp-icon-btn danger"
                    onClick={() => deleteTopup(t)}
                    data-testid={`expenses-topup-delete-${t.id}`}
                    title="Delete top-up"
                  >
                    <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="expenses-balance-done"
            onClick={() => onOpenChange(false)}
            style={{
              width: "100%", background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function MiniTile({ label, value, sub, strong = false, danger = false, testId }) {
  return (
    <div data-testid={testId} style={{
      border: `1px solid ${BORDER}`, borderRadius: 10, padding: "8px 10px",
      background: "#F9F8F6",
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, letterSpacing: "0.16em",
        textTransform: "uppercase", color: LABEL_GREEN,
      }}>{label}</div>
      <div style={{
        fontSize: 16, fontWeight: 700, marginTop: 2,
        color: danger ? "#B4351B" : (strong ? DARK_GREEN : TEXT),
        fontVariantNumeric: "tabular-nums",
      }}>{value}</div>
      <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

// ============================================================================
function ReceiptPreviewDialog({ record, onClose }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  useBackClosesDialog(!!record, onClose, "preview");

  useEffect(() => {
    if (!record) { setPreview(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/expenses/records/${record.id}/receipt-preview`);
        setPreview(res.data || null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Preview failed");
        setPreview(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [record]);

  const downloadOriginal = async () => {
    try {
      // Build friendly filename: TeamX_MMDDYYYY_Description.ext
      // e.g. Team1_08102026_Gas.jpeg · HQ_08252026_Repairs.pdf
      const teamLabel = /^team_\d+$/.test(record.team || "") ? `Team${record.team.split("_")[1]}`
        : record.team === "hq" ? "HQ" : "Team";
      const d = fromYMD(record.date) || new Date();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      const dateStr = `${mm}${dd}${yyyy}`;
      const descSlug = (record.description || "receipt")
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "_") || "receipt";
      const orig = record.receipt_filename || "";
      const ext = orig.includes(".") ? orig.slice(orig.lastIndexOf(".")) : "";
      const filename = `${teamLabel}_${dateStr}_${descSlug}${ext}`;
      const res = await api.get(`/expenses/records/${record.id}/receipt`, {
        responseType: "blob",
        params: { disposition: "attachment", filename },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="expenses-receipt-preview-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Receipt preview</DialogTitle>
          <DialogDescription>
            {record?.receipt_filename || "Receipt"} · {record?.date ? format(fromYMD(record.date) || new Date(), "MMM d, yyyy") : ""}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div style={{ color: MUTED, padding: 30, textAlign: "center" }}>Loading preview…</div>
        ) : !preview || !preview.page_images?.length ? (
          <div style={{ color: MUTED, padding: 30, textAlign: "center" }}>
            No preview available.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {preview.page_images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Page ${i + 1}`}
                data-testid={`expenses-receipt-page-${i + 1}`}
                style={{
                  width: "100%", height: "auto",
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                }}
              />
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <button
            type="button"
            onClick={downloadOriginal}
            data-testid="expenses-receipt-download-btn"
            style={{
              flex: 1, background: "#fff", color: DARK_GREEN,
              border: `1px solid rgba(15,59,46,0.25)`,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Download original
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="expenses-receipt-close-btn"
            style={{
              flex: 1, background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
